import { useState, useEffect, useRef, useCallback } from 'react';

interface UseViewportPageSizeOptions {
  rowHeight: number;
  minPageSize?: number;
  maxPageSize?: number;
  bufferSpace?: number;
  /** Pass loading state - hook will recalculate when loading becomes false */
  loading?: boolean;
}

/**
 * Calculate an initial page size estimate based on viewport height.
 * This provides a better starting value than minPageSize while waiting for DOM to be ready.
 * Assumes typical layout: ~200px for header/nav/filters, ~50px for pagination footer.
 */
function getInitialEstimate(
  rowHeight: number,
  minPageSize: number,
  maxPageSize: number,
  bufferSpace: number
): number {
  if (typeof window === 'undefined') return minPageSize;

  const viewportHeight = window.innerHeight;
  // Estimate: header ~64px, page title/filters ~120px, pagination footer ~50px, buffer
  const estimatedTopOffset = 200;
  const estimatedFooterHeight = 50;
  const availableHeight = viewportHeight - estimatedTopOffset - estimatedFooterHeight - bufferSpace;
  const estimated = Math.floor(availableHeight / rowHeight);

  return Math.max(minPageSize, Math.min(maxPageSize, estimated));
}

export function useViewportPageSize(options: UseViewportPageSizeOptions) {
  const {
    rowHeight,
    minPageSize = 5,
    maxPageSize = 100,
    bufferSpace = 20,
    loading = false,
  } = options;

  // Start with a viewport-based estimate instead of minPageSize
  const [pageSize, setPageSize] = useState<number>(() =>
    getInitialEstimate(rowHeight, minPageSize, maxPageSize, bufferSpace)
  );
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const containerRef = useRef<HTMLElement | null>(null);
  const hasCalculatedRef = useRef(false);

  const calculatePageSize = useCallback(() => {
    const viewportHeight = window.innerHeight;

    // Find the table container
    const tableContainer = document.querySelector('[data-table-container]') as HTMLElement;

    if (!tableContainer) {
      return false; // Indicate failure to find container
    }

    containerRef.current = tableContainer;

    // Get the position of the table container relative to viewport
    const containerRect = tableContainer.getBoundingClientRect();
    const topOffset = containerRect.top;

    // Find the pagination footer by data attribute
    const footer = document.querySelector('[data-pagination-footer]') as HTMLElement;
    let footerHeight = 0;

    if (footer) {
      footerHeight = footer.getBoundingClientRect().height;
    }

    // Calculate available height
    // viewport - distance from top - footer height - buffer
    const availableHeight = viewportHeight - topOffset - footerHeight - bufferSpace;

    const calculatedSize = Math.floor(availableHeight / rowHeight);
    const newSize = Math.max(minPageSize, Math.min(maxPageSize, calculatedSize));

    setPageSize(newSize);
    hasCalculatedRef.current = true;
    return true;
  }, [rowHeight, minPageSize, maxPageSize, bufferSpace]);

  // Setup ResizeObserver and initial calculation
  useEffect(() => {
    // Use ResizeObserver for reliable detection of container availability and size changes
    resizeObserverRef.current = new ResizeObserver(() => {
      calculatePageSize();
    });

    // Initial calculation with retry logic for DOM readiness
    let retryCount = 0;
    const maxRetries = 20;
    const retryDelay = 100;

    const tryCalculate = () => {
      const success = calculatePageSize();

      if (success && containerRef.current && resizeObserverRef.current) {
        // Start observing the container for size changes
        resizeObserverRef.current.observe(containerRef.current);
      } else if (!success && retryCount < maxRetries) {
        retryCount++;
        setTimeout(tryCalculate, retryDelay);
      }
    };

    // Start trying immediately
    const timer = setTimeout(tryCalculate, 0);

    // Also listen to window resize for viewport changes
    const handleResize = () => calculatePageSize();
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [calculatePageSize]);

  // Recalculate when loading changes from true to false
  // This handles cases where the container becomes visible after loading completes
  useEffect(() => {
    if (!loading) {
      // Small delay to ensure DOM has updated after loading state change
      const timer = setTimeout(() => {
        const success = calculatePageSize();

        // If we found the container and it's a new one, start observing it
        if (success && containerRef.current && resizeObserverRef.current) {
          // Disconnect from old container if any
          resizeObserverRef.current.disconnect();
          // Observe the new container
          resizeObserverRef.current.observe(containerRef.current);
        }
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [loading, calculatePageSize]);

  return pageSize;
}
